import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import ContextMenu from './ContextMenu.mjs';
import * as FileService from '../services/FileService.mjs';
import "../css/File.css";
import { buildThumbnailUrl, thumbnailCacheKey } from '../utils/thumbnailUrl';
import { roleOf, staleByOf, staleExportsOf } from '../utils/fileFacts';
import { useFileBadges, categoryForMimetype, BADGE_DEFS } from '../context/FileBadgeContext';

// Human-readable byte size: B / KB / MB / GB.
const formatBytes = (bytes) => {
    if (bytes === null || bytes === undefined || isNaN(bytes)) return null;
    if (bytes < 1024) return `${bytes} B`;
    const units = ['KB', 'MB', 'GB', 'TB'];
    let value = bytes / 1024;
    let unitIndex = 0;
    while (value >= 1024 && unitIndex < units.length - 1) {
        value /= 1024;
        unitIndex += 1;
    }
    const rounded = value >= 100 ? Math.round(value) : Math.round(value * 10) / 10;
    return `${rounded} ${units[unitIndex]}`;
};

// Seconds -> m:ss or h:mm:ss.
const formatDuration = (seconds) => {
    if (seconds === null || seconds === undefined || isNaN(seconds) || seconds < 0) return null;
    const total = Math.floor(seconds);
    const hrs = Math.floor(total / 3600);
    const mins = Math.floor((total % 3600) / 60);
    const secs = total % 60;
    const pad = (n) => String(n).padStart(2, '0');
    if (hrs > 0) return `${hrs}:${pad(mins)}:${pad(secs)}`;
    return `${mins}:${pad(secs)}`;
};

// Derive a short uppercase TYPE label from the mimetype (subtype) or file name.
const formatType = (mimetype, name) => {
    if (mimetype && mimetype.includes('/')) {
        const subtype = mimetype.split('/')[1].split(';')[0].trim();
        if (subtype) {
            // Normalise common composite subtypes (e.g. svg+xml -> SVG, x-wav -> WAV).
            const cleaned = subtype.split('+')[0].replace(/^x-/, '');
            return cleaned.toUpperCase().slice(0, 6);
        }
    }
    if (name && name.includes('.')) {
        const ext = name.split('.').pop();
        if (ext) return ext.toUpperCase().slice(0, 6);
    }
    return null;
};

const FileIcon = ({ file, isSelected, onFileClick, onCheckboxClick, onContextSelect }) => {
    const navigate = useNavigate();
    const { isBadgeEnabled } = useFileBadges();
    const [contextMenuPos, setContextMenuPos] = useState(null);
    const [shouldPlay, setShouldPlay] = useState(false);
    const [progress, setProgress] = useState(0); // 0..1 playback progress for waveform overlay
    const [thumbnailUrl, setThumbnailUrl] = useState(null); // <-- now async state
    const [downloadUrl, setDownloadUrl] = useState(null);   // <-- now async state
    const audioRef = useRef(null);
    const hoverTimeoutRef = useRef(null);
    const rafRef = useRef(null);
    // Tracks whether the pointer is still over this card. The signed-URL fetch in
    // handleMouseEnter is async, so we must re-check this once it resolves before
    // starting playback — otherwise a preview starts after the cursor has left.
    const isHoveringRef = useRef(false);

    const isAudio = file.metadata.mimetype && file.metadata.mimetype.startsWith('audio/');
    const isVideo = file.metadata.mimetype && file.metadata.mimetype.startsWith('video/');
    const isImage = file.metadata.mimetype && file.metadata.mimetype.startsWith('image/');

    // Unresolved (unchecked) notes count, denormalized onto the file doc by the
    // backend. Drives the small corner indicator so files surfaced by the
    // "Unresolved notes only" search filter are recognisable at a glance.
    const openNotes = file.noteSummary?.open || 0;

    // Where this file sits in the authoring pipeline, and whether the pipeline
    // has drifted. A `.blend` is unrenderable, so without the role marker its
    // tile is indistinguishable from a `.zip`; the staleness pip is the only
    // place outside the details modal that says an export needs redoing.
    const role = roleOf(file);
    const staleBy = staleByOf(file);
    const staleExports = staleExportsOf(file);
    const staleNote = staleBy > 0
        ? `${staleBy} version${staleBy === 1 ? '' : 's'} behind its source`
        : staleExports > 0
            ? `${staleExports} file${staleExports === 1 ? '' : 's'} made from this are out of date`
            : null;

    // At-a-glance metadata pills. Each is shown only when (a) the user has
    // enabled that badge for this file-type category (per-user setting) and
    // (b) its underlying value actually exists. Order follows BADGE_DEFS.
    const badgeCategory = categoryForMimetype(file.metadata.mimetype);
    const badgeValues = {
        type: formatType(file.metadata.mimetype, file.metadata.name),
        size: formatBytes(file.metadata.size),
        duration: (isAudio || isVideo) ? formatDuration(file.metadata.duration) : null,
        dimensions: (isImage || isVideo) && file.metadata.width && file.metadata.height
            ? `${file.metadata.width}×${file.metadata.height}`
            : null,
    };
    const visibleBadges = BADGE_DEFS.filter(
        ({ key }) => isBadgeEnabled(badgeCategory, key) && badgeValues[key]
    );

    // Thumbnail streams through the backend (lazy generation) so it uses the
    // short-lived auth_token fallback for <img>. The URL is versioned by the
    // thumbnail's own identity so replacing one busts the year-long cache.
    const thumbnailVersion = thumbnailCacheKey(file);
    useEffect(() => {
        buildThumbnailUrl(file).then(setThumbnailUrl);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [file.id, thumbnailVersion]);

    // SEC-7: the audio preview uses a signed GCS URL (no token embedded).
    const handleMouseEnter = () => {
        if (!isAudio) return;
        isHoveringRef.current = true;
        hoverTimeoutRef.current = setTimeout(async () => {
            try {
                const freshUrl = await FileService.getSignedDownloadUrl(file.id);
                // The fetch above is async: the pointer may have left (or the card
                // scrolled out of view) while it was in flight. Bail out so we don't
                // start a preview the user can no longer stop by un-hovering — this
                // is the main cause of audio that "keeps playing after I moved away".
                if (!isHoveringRef.current) return;
                setDownloadUrl(freshUrl);
                setShouldPlay(true);
            } catch (err) {
                console.warn("Could not load audio preview", err);
            }
        }, 500);
    };

    // ... rest of handlers unchanged ...

    useEffect(() => {
        if (shouldPlay && audioRef.current) {
            const playPromise = audioRef.current.play();
            if (playPromise !== undefined) {
                playPromise.catch(err => {
                    // AbortError fires when a newer load()/pause() supersedes this
                    // play() (rapid hover or scrolling). It's transient, so ignore it
                    // — tearing down on it is why previews "sometimes don't play".
                    if (err?.name === 'AbortError') return;
                    console.warn("Audio play interrupted or blocked", err);
                    setShouldPlay(false);
                });
            }
            // Drive the progress overlay from rAF (~60fps) instead of the audio
            // `timeupdate` event, which only fires ~4x/sec and looks choppy.
            const tick = () => {
                const audio = audioRef.current;
                if (audio) {
                    const { currentTime, duration } = audio;
                    if (duration && isFinite(duration) && duration > 0) {
                        setProgress(currentTime / duration);
                    }
                }
                rafRef.current = requestAnimationFrame(tick);
            };
            rafRef.current = requestAnimationFrame(tick);
        } else if (!shouldPlay && audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            setProgress(0);
        }
        return () => {
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
        };
    }, [shouldPlay]);

    // On unmount — e.g. a virtualized card scrolling out of view — explicitly stop
    // and release the audio. Removing the <audio> element from the DOM does NOT
    // reliably pause it, so without this a preview keeps playing after you've
    // scrolled past the card (and there's no longer any card to un-hover).
    useEffect(() => {
        // Capture the node now: it's stable for the card's lifetime (isAudio never
        // changes per instance), and reading a ref in cleanup is lint-flagged.
        const audio = audioRef.current;
        return () => {
            isHoveringRef.current = false;
            if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            if (audio) {
                try {
                    audio.pause();
                    audio.removeAttribute('src');
                    audio.load();
                } catch (err) {
                    // Ignore teardown errors.
                }
            }
        };
    }, []);

    const handleMouseLeave = () => {
        if (!isAudio) return;
        isHoveringRef.current = false;
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
        }
        setShouldPlay(false);
    };

    const handleContextMenu = (e) => {
        e.preventDefault(); e.stopPropagation();
        setContextMenuPos({ x: e.clientX, y: e.clientY });
        // Right-clicking outside the current selection targets just this file —
        // adding to the selection meant the menu's action quietly applied to
        // whatever was selected before, too. Right-clicking something already
        // selected leaves a multi-file selection intact.
        if (!isSelected) onContextSelect?.(file.id);
    };

    const onContainerClick = (e) => onFileClick(file.id, e);
    const onCheckboxChange = (e) => { e.stopPropagation(); onCheckboxClick(file.id); };
    const downloadFile = () => FileService.downloadFile(file.id, file.metadata.name);

    return (
        <>
            <div
                className={`file-card ${isSelected ? 'selected' : ''} ${shouldPlay ? 'playing-audio' : ''}`}
                onClick={onContainerClick}
                onContextMenu={handleContextMenu}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                // Grid cards were mouse-only while the table rows were already
                // focusable (L-13), so switching to grid view lost keyboard users
                // the ability to open a file at all. Enter opens details; Space
                // toggles selection, the file-manager idiom (and preventDefault
                // stops it scrolling the results).
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        onFileClick(file.id, e);
                    } else if (e.key === ' ') {
                        e.preventDefault();
                        onCheckboxClick(file.id);
                    }
                }}
                aria-selected={isSelected}
            >
                <div className="file-thumb-wrapper">
                    {/* Only render once we have a valid URL */}
                    {thumbnailUrl && (
                        <img
                            src={thumbnailUrl}
                            alt={file.metadata.name}
                            loading="lazy"
                            decoding="async"
                            width="150"
                            height="150"
                        />
                    )}
                    {isAudio && shouldPlay && (
                        <div
                            className="file-progress-overlay"
                            style={{ width: `${progress * 100}%` }}
                        />
                    )}
                    {shouldPlay && (
                        <div className="audio-playing-overlay">
                            <span>🔊</span>
                        </div>
                    )}
                    {openNotes > 0 && (
                        <div
                            className="file-note-indicator"
                            title={`${openNotes} unresolved note${openNotes === 1 ? '' : 's'}`}
                            aria-label={`${openNotes} unresolved note${openNotes === 1 ? '' : 's'}`}
                        >
                            <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true">
                                <path
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
                                />
                            </svg>
                            <span className="file-note-indicator-count">{openNotes}</span>
                        </div>
                    )}
                    {role && (
                        <div
                            className={`file-role-indicator file-role-indicator--${role}`}
                            title={role === 'export' ? 'Made from a source file' : `${role === 'raw' ? 'Raw' : 'Source'} file`}
                            aria-label={role === 'export' ? 'Made from a source file' : `${role === 'raw' ? 'Raw' : 'Source'} file`}
                        >
                            <svg viewBox="0 0 24 24" width="11" height="11" aria-hidden="true">
                                <path
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M10 13a5 5 0 0 0 7.07 0l2.5-2.5a5 5 0 0 0-7.07-7.07L11.5 4.43M14 11a5 5 0 0 0-7.07 0l-2.5 2.5a5 5 0 0 0 7.07 7.07L12.5 19.5"
                                />
                            </svg>
                        </div>
                    )}
                    {staleNote && (
                        <div className="file-stale-indicator" title={staleNote} aria-label={staleNote}>
                            <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true">
                                <path
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M12 8v5M12 16h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"
                                />
                            </svg>
                        </div>
                    )}
                </div>

                <div className="file-info">
                    <p className="file-name" title={file.metadata.name}>
                        {file.metadata.name}
                    </p>
                    {visibleBadges.length > 0 && (
                        <div className="file-badges">
                            {visibleBadges.map(({ key }) => (
                                <span key={key} className="file-badge">{badgeValues[key]}</span>
                            ))}
                        </div>
                    )}
                </div>

                <div className="file-select-checkbox" onClick={(e) => e.stopPropagation()}>
                    <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={onCheckboxChange}
                        aria-label={`Select ${file.metadata.name}`}
                    />
                </div>

                {isAudio && (
                    <audio
                        ref={audioRef}
                        src={shouldPlay ? downloadUrl : undefined}
                        loop={true}
                        muted={false}
                        style={{ display: 'none' }}
                    />
                )}
            </div>

            {contextMenuPos && createPortal(
                <ContextMenu
                    position={contextMenuPos}
                    onClose={() => setContextMenuPos(null)}
                    aria-label={`Actions for ${file.metadata.name}`}
                >
                    <button role="menuitem" onClick={downloadFile}>Download</button>
                    <button role="menuitem" onClick={() => onFileClick(file.id, { ctrlKey: false, shiftKey: false })}>Details</button>
                    {isAudio && <button role="menuitem" onClick={() => navigate(`/split/${file.id}`)}>Split audio…</button>}
                </ContextMenu>,
                document.body
            )}
        </>
    );
};

export default FileIcon;
